const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  is_first_login: boolean;
}

export interface UserMe {
  id: string;
  full_name: string;
  email: string;
  username: string | null;
  phone: string | null;
  role: string;
  center_id: string | null;
  center_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  trial_end_date: string | null;
}

export const authService = {
  login: async (
    identifier: string,
    password: string
  ): Promise<TokenResponse> => {
    const res = await fetch(`${BASE_URL}/auth/login-identifier`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: identifier, password }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      // Callers pass the message through t(); backend text is shown as-is.
      throw new Error(data?.detail ?? data?.message ?? "auth.loginError");
    }

    return res.json();
  },

  logout: async () => {
    const fcmToken = sessionStorage.getItem("fcm_token");
    if (fcmToken) {
      const authToken = sessionStorage.getItem("access_token");
      if (authToken) {
        try {
          // Raw fetch instead of api wrapper — the wrapper auto-redirects on 401,
          // which would be disruptive when logging out with an already-expired token.
          await fetch(
            `${BASE_URL}/users/me/devices/${encodeURIComponent(fcmToken)}`,
            {
              method: "DELETE",
              headers: { Authorization: `Bearer ${authToken}` },
            }
          );
        } catch {
          // best-effort — don't block logout on failure
        }
      }
      sessionStorage.removeItem("fcm_token");
    }
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("current_user");
    localStorage.removeItem("refresh_token");
  },

  isLoggedIn: () => !!sessionStorage.getItem("access_token"),

  getMe: async (): Promise<UserMe> => {
    const token = sessionStorage.getItem("access_token");
    const res = await fetch(`${BASE_URL}/users/me`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error("Failed to fetch user info");
    const user: UserMe = await res.json();
    sessionStorage.setItem("current_user", JSON.stringify(user));
    return user;
  },

  getStoredUser: (): UserMe | null => {
    try {
      const raw = sessionStorage.getItem("current_user");
      return raw ? (JSON.parse(raw) as UserMe) : null;
    } catch {
      return null;
    }
  },
};
